import React, { useState } from 'react';
import {useDispatch, useSelector} from "react-redux";

import { t } from "../common/LocalizationProvider";
import ReportLayoutPage from "./ReportLayoutPage";
import {formatDistance, formatDate, formatWeight} from '../common/formatter';
import {devicesSelectedActions} from "../store";

import {
    Button,
    FormControl,
    Grid, IconButton,
    InputLabel,
    makeStyles,
    MenuItem,
    Select, Snackbar,
    TextField,
    Typography,
    Paper, Portal
} from "@material-ui/core";
import {DataGrid} from "@material-ui/data-grid";
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import Alert from "@material-ui/lab/Alert";
import {cyberStyles} from "../cssShared/cyberTheme";

const useStyles = makeStyles(theme => ({
    ...cyberStyles(theme),
    deleteIcon: {
        color: '#ff4d4d',
        '&:hover': {
            color: '#ff1a1a',
            filter: 'drop-shadow(0 0 5px rgba(255, 77, 77, 0.5))',
        }
    },
}));

const Filter = ({ setItems }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const devices = useSelector(state => Object.values(state.devices.items));
    const devicesSelected = useSelector(state => state.devicesSelected.devicesSelected ? state.devicesSelected.devicesSelected : [])

    const [deviceId, setDeviceId] = useState(devicesSelected);
    const [distance, setDistance] = useState();
    const [weight, setWeight] = useState();
    const [distanceOfThePart, setDistanceOfThePart] = useState([]);
    const [to, setTo] = useState([]);
    const [legs, setLegs] = useState([])
    const [openSnackbar, setOpenSnackbar] = useState(null);

    const showOnly = false;

    const handleSelectChange = (event) => {
        const selectedDeviceId = event.target.value;
        setDeviceId(selectedDeviceId);
        dispatch(devicesSelectedActions.updateDevicesSelected(selectedDeviceId));
    };

    const handleClick = (mail, json) => {
        const accept = json ? 'application/json' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        handleSubmit(deviceId, mail, { Accept: accept });
    }

    const handleSubmit = async (deviceId, mail) => {
        const item = {distanceOfThePart: distanceOfThePart, to: to}
        const query = new URLSearchParams({ mail });
        deviceId.forEach(deviceIdS => query.append("deviceId", deviceIdS))

        const response = await fetch(`/api/reports/simulator?${query.toString()}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json'},
            body: JSON.stringify(item),
        });
        if (response.ok) {
            if (mail)
                setOpenSnackbar('EmailReportAlert')
            const contentType = response.headers.get('content-type');
            if (contentType) {
                if (contentType === 'application/json') {
                    const res = await response.json();
                    setItems(res);
                    if (res.length === 0)
                        setOpenSnackbar('deviceConfiguration')
                } else {
                    window.location.assign(window.URL.createObjectURL(await response.blob()));
                    setOpenSnackbar('ExcelFileExported')
                }
            }
        }
        else if (response.status === 400) {
            const errorText = await response.text();
            let spliteText = errorText.split(' ')[0];
            setOpenSnackbar(spliteText)
        }
    }

    const handleAddLegs = () => {
        const newLeg ={
            id: legs.length + 1,
            lf: distance,
            distance: weight
        }
        setLegs([...legs, newLeg]);
        setDistanceOfThePart([...distanceOfThePart, distance]);
        setTo([...to, weight])
    }

    const handleDelete = (id) => {
        setLegs(legs.filter((_, index) => index !== id));
        setDistanceOfThePart(distanceOfThePart.filter((_, index) => index !== id));
        setTo(to.filter((_, index) => index !== id));
    }

    const handleCloseSnackbar = () => {
        setOpenSnackbar(null);
    };

    return (
        <>
            <Grid container spacing={2} className={classes.gridContainer}>
                <Grid item xs={12} sm={6}>
                    <FormControl variant="filled" fullWidth className={classes.formControl}>
                        <InputLabel>{t('sharedDevice')}</InputLabel>
                        <Select
                            multiple
                            value={deviceId || ''}
                            onChange={handleSelectChange}
                            MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
                        >
                            {devices.map((device) => (
                                <MenuItem className={classes.menuItem} key={device.id} value={device.id}>{device.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        className={classes.neonInput}
                        value={distance || ''}
                        onChange={event => setDistance(event.target.value)}
                        label={t('sharedDistance')+': \'Km\''}
                        variant="filled"
                        fullWidth />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        className={classes.neonInput}
                        value={weight || ''}
                        onChange={event => setWeight( event.target.value)}
                        label={t('Weight')+': \'Metric Tons\''}
                        variant="filled"
                        fullWidth />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Button
                        size="large"
                        variant="outlined"
                        className={classes.cyberButton}
                        startIcon={<AddIcon />}
                        onClick={handleAddLegs}
                        fullWidth
                    >
                        {t('sharedAddLegs')}
                    </Button>
                </Grid>
            </Grid>
            {legs.map((leg, index) => (
                <Grid key={index} container spacing={2} className={classes.gridContainer} alignItems="center">
                    <Grid item xs={5} sm={3}>
                        <TextField
                            className={classes.neonInput}
                            value={leg.lf}
                            variant="outlined"
                            fullWidth
                            InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={5} sm={3}>
                        <TextField
                            className={classes.neonInput}
                            value={leg.distance }
                            variant="outlined"
                            fullWidth
                            InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={2}>
                        <IconButton onClick={() => handleDelete(index)}>
                            <DeleteIcon className={classes.deleteIcon}/>
                        </IconButton>
                    </Grid>
                </Grid>
            ))}
            <Grid container spacing={2} className={classes.gridContainer}>
                <Grid item xs={!showOnly ? 4 : 12} sm={2}>
                    <Button
                        onClick={() => handleClick(false, true)}
                        variant='outlined'
                        className={classes.cyberButton}
                        fullWidth>
                        {t('reportShow')}
                    </Button>
                </Grid>
                {!showOnly &&
                    <Grid item xs={4} sm={2}>
                        <Button
                            onClick={() => handleClick(false, false)}
                            variant='outlined'
                            className={classes.cyberButton}
                            fullWidth>
                            {t('reportExport')}
                        </Button>
                    </Grid>}
                {!showOnly &&
                    <Grid item xs={4} sm={2}>
                        <Button
                            onClick={() => handleClick(true, false)}
                            variant='outlined'
                            className={classes.cyberButton}
                            fullWidth>
                            <Typography variant="button" noWrap>{t('reportEmail')}</Typography>
                        </Button>
                    </Grid>}
            </Grid>

            <Portal disablePortal={false}>
            <Snackbar open={!!openSnackbar} autoHideDuration={12000} onClose={handleCloseSnackbar} >
                <Alert onClose={handleCloseSnackbar} variant="filled" severity="error">
                    {t(openSnackbar)}
                </Alert>
            </Snackbar>
            </Portal>
        </>
    );
}

const Emissions = () =>{
    const classes = useStyles();
    const [items, setItems] = useState([]);
    const columns = [
        {
            headerName: t('positionFixTime'),
            field: 'serverTime',
            type: 'dateTime',
            width: 190,
            // width: theme.dimensions.columnWidthDate,
            valueFormatter: ({ value }) => formatDate(value),
        },{
            headerName: t('positionTotalDistance'),
            field: 'totalDistance',
            type: 'number',
            width: 150,
            // width: theme.dimensions.columnWidthNumber,
            valueFormatter: ({ value }) => formatDistance(value, 'km'),
        },{
            headerName: t('positionDistanceLeg'),
            field: 'legDistance',
            type: 'number',
            width: 150,
            // width: theme.dimensions.columnWidthNumber,
            valueFormatter: ({ value }) => formatDistance(value, 'km'),
        },{
            headerName: t('GHG_Emission'),
            field: 'ghgEmissionSingle',
            type: 'number',
            width: 220,
            // width: theme.dimensions.columnWidthDate,
            valueFormatter: ({ value }) => formatWeight(value, 'metric_tons'),
        },{
            headerName: t('blackCarbonGHG'),
            // field: 'serverTime',
            field: 'blackCarbonSingle',
            type: 'number',
            width: 190,
            valueFormatter: ({ value }) => formatWeight(value, 'grams'),
        }
    ];

    return (
        <ReportLayoutPage filter={<Filter setItems={setItems} />}>
            <Paper className={classes.paper}>
                <DataGrid
                    rows={items}
                    columns={columns}
                    hideFooterPagination
                    autoHeight
                    getRowId={() => Math.random()}/>
            </Paper>
        </ReportLayoutPage>
    );
}

export default Emissions;